/**
 * Resolves the template the doctor's encounters should default to.
 *
 * Resolution order:
 *   1. The tenant's default template for the doctor's specialty
 *      (`GET /templates/default/:specialty`).
 *   2. If specialty is null / 'general' / no default exists for the
 *      doctor's specialty: fall through to the tenant's `general`
 *      default. This matches the product call: "specialty unset routes
 *      to general SOAP unless changed."
 *   3. If even the general default is missing (clinic deleted it): the
 *      hook returns `null`. Callers should fall back to a generic SOAP
 *      shape rather than blocking the encounter.
 *
 * The lookup is cached per (specialty key) by React Query so opening
 * many encounters in a row doesn't refetch.
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import type { Template } from '@/services/api/templatesApi';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import { useMyProfile } from './useMyProfile';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const GENERAL = 'general';

async function fetchDefault(specialty: string): Promise<Template | null> {
  try {
    const res = await apiClient.get<ApiEnvelope<Template>>(
      `/templates/default/${encodeURIComponent(specialty)}`,
    );
    return res.data?.data ?? null;
  } catch (err) {
    // 404 from the backend means "no default set for this specialty" —
    // not an error, just a fall-through signal.
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export const defaultTemplateKeys = {
  bySpecialty: (specialty: string) =>
    ['templates', 'default', specialty] as const,
};

export interface DefaultTemplateState {
  /** Resolved template — specialty default, general fallback, or null. */
  template: Template | null;
  loading: boolean;
  /** Which specialty key the resolved template came from (for logging/UI). */
  resolvedFrom: string | null;
}

export function useDefaultTemplate(): DefaultTemplateState {
  const profile = useMyProfile();
  /* A role that cannot read templates (an accountant) must not ask — the answer
     is a 403 in the console on every page the shell mounts this under. */
  const canRead = useAuthStore((s) => s.roleCheck.hasPermission(Permission.TEMPLATE_READ));
  const rawSpecialty = profile.data?.specialty;
  const specialty = rawSpecialty && rawSpecialty.length > 0 ? rawSpecialty : null;

  // Specialty-specific lookup. Skipped when specialty is null/general —
  // we fall straight through to the general lookup below.
  const specialtyQuery = useQuery({
    queryKey: defaultTemplateKeys.bySpecialty(specialty ?? ''),
    queryFn: () => fetchDefault(specialty as string),
    enabled: canRead && !!specialty && specialty !== GENERAL,
    staleTime: 5 * 60_000,
    retry: false,
  });

  // General fallback. Always fetched (cheap, cached) so we never block
  // on a second round-trip when the specialty lookup returns null.
  const generalQuery = useQuery({
    queryKey: defaultTemplateKeys.bySpecialty(GENERAL),
    queryFn: () => fetchDefault(GENERAL),
    enabled: canRead && profile.data !== undefined,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (profile.isLoading) {
    return { template: null, loading: true, resolvedFrom: null };
  }

  if (specialty && specialty !== GENERAL) {
    if (specialtyQuery.isLoading) {
      return { template: null, loading: true, resolvedFrom: null };
    }
    if (specialtyQuery.data) {
      return {
        template: specialtyQuery.data,
        loading: false,
        resolvedFrom: specialty,
      };
    }
    // Specialty exists but no default for it — fall through to general.
  }

  if (generalQuery.isLoading) {
    return { template: null, loading: true, resolvedFrom: null };
  }
  return {
    template: generalQuery.data ?? null,
    loading: false,
    resolvedFrom: generalQuery.data ? GENERAL : null,
  };
}
